/** The drawing layer's coordinate contract — the TS mirror of
 * `server/src/graphmind/services/drawing_geometry.py` (KEEP IN SYNC).
 *
 * Scene units are Excalidraw's own. For a PDF document, page n (1-based) is a
 * locked image element of size (points_w * SCENE_SCALE, points_h * SCENE_SCALE),
 * stacked at x=0, y=pageTop(n) with PAGE_GAP scene units between pages. The
 * layout is recorded in `appState.gm_pages` so the server can map elements back
 * onto PDF pages (flatten, OCR) without re-deriving anything.
 *
 * pdfjs `getViewport({ scale: SCENE_SCALE })` and pypdf `mediabox * SCENE_SCALE`
 * produce identical dimensions, so both sides land on the same page pixels.
 */

/** Scene units per PDF point. */
export const SCENE_SCALE = 1.5;
/** Scene units between stacked PDF pages. */
export const PAGE_GAP = 40;

export interface PageRect {
  /** 1-based PDF page number. */
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The canonical stacked layout for a PDF's pages. `sizesPt` is each page's
 * intrinsic (scale-1 viewport) size in PDF points, index 0 = page 1. */
export function pageLayout(sizesPt: { w: number; h: number }[]): PageRect[] {
  const rects: PageRect[] = [];
  let top = 0;
  for (const [i, size] of sizesPt.entries()) {
    const w = size.w * SCENE_SCALE;
    const h = size.h * SCENE_SCALE;
    rects.push({ page: i + 1, x: 0, y: top, w, h });
    top += h + PAGE_GAP;
  }
  return rects;
}

/** Parse a stored `appState.gm_pages` defensively (malformed entries skipped). */
export function pagesFromAppState(appState: Record<string, unknown> | undefined): PageRect[] {
  const raw = appState?.gm_pages;
  if (!Array.isArray(raw)) return [];
  const rects: PageRect[] = [];
  for (const entry of raw) {
    if (typeof entry !== "object" || entry === null) continue;
    const e = entry as Record<string, unknown>;
    const page = Number(e.page);
    const x = Number(e.x);
    const y = Number(e.y);
    const w = Number(e.w);
    const h = Number(e.h);
    if (Number.isFinite(page) && page >= 1 && w > 0 && h > 0 && Number.isFinite(x) && Number.isFinite(y)) {
      rects.push({ page, x, y, w, h });
    }
  }
  return rects.sort((a, b) => a.page - b.page);
}
